import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/react-hooks';
import { AgGridColumn } from '@ag-grid-community/react';
import { get } from 'lodash';
import moment from 'moment';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Cell,
  LabelList,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import Card from '../../common/components/Card';
import DateRangePicker from '../../common/components/DateRangePicker';
import DataGrid from '../../common/components/DataGrid';
import TextLoader from '../../common/components/TextLoader';
import { GET_TIMESHEET_LATENESS_BY_WEEK, GET_TIMESHEET_LATENESS_DETAILS } from '../queries';

const formatEndDate = endDate => moment(endDate, 'YYYYMMDD').format('MM/DD/YYYY');

const TimesheetLatenessReport = () => {
  const [startDate, setStartDate] = useState(moment().subtract(11, 'weeks').startOf('isoWeek'));
  const [endDate, setEndDate] = useState(moment().endOf('isoWeek'));

  const { data, loading, error } = useQuery(GET_TIMESHEET_LATENESS_BY_WEEK, {
    variables: {
      startDate: startDate.format('YYYYMMDD'),
      endDate: endDate.format('YYYYMMDD'),
    },
  });

  const [selectedEndDate, setSelectedEndDate] = useState(null);

  const reportItems = useMemo(() => {
    const items = get(data, 'finance.timesheetLatenessByWeek') || [];
    return items.map(item => ({
      endDate: item.endDate,
      weekEnding: formatEndDate(item.endDate),
      lateCount: item.lateCount || 0,
    }));
  }, [data]);

  useEffect(() => {
    if (!reportItems.length) {
      setSelectedEndDate(null);
      return;
    }
    if (!selectedEndDate || !reportItems.find(item => item.endDate === selectedEndDate)) {
      setSelectedEndDate(reportItems[reportItems.length - 1].endDate);
    }
  }, [reportItems, selectedEndDate]);

  const {
    data: detailsData,
    loading: detailsLoading,
    error: detailsError,
  } = useQuery(GET_TIMESHEET_LATENESS_DETAILS, {
    skip: !selectedEndDate,
    variables: {
      endDate: selectedEndDate,
    },
  });

  const rollingAverageWindow = 4;
  const rollingAverages = useMemo(() => {
    if (!reportItems.length) {
      return [];
    }
    return reportItems.map((item, index) => {
      const windowStart = Math.max(0, index - (rollingAverageWindow - 1));
      const windowItems = reportItems.slice(windowStart, index + 1);
      const totalLate = windowItems.reduce((sum, current) => sum + current.lateCount, 0);
      return totalLate / windowItems.length;
    });
  }, [reportItems]);

  const chartData = useMemo(
    () =>
      reportItems.map((item, index) => ({
        week: item.weekEnding,
        lateCount: item.lateCount,
        avgLateCount: rollingAverages[index] ?? 0,
        endDate: item.endDate,
      })),
    [reportItems, rollingAverages],
  );

  const detailRows = useMemo(() => {
    const items = get(detailsData, 'finance.timesheetLatenessDetails') || [];
    return items.map(item => ({
      firstName: item.firstName,
      lastName: item.lastName,
      email: item.email,
      status: item.status,
      submittedAt: item.submittedAt
        ? moment(item.submittedAt).format('MM/DD/YYYY h:mm A')
        : '—',
      cutoff: item.cutoff ? moment(item.cutoff).format('MM/DD/YYYY h:mm A') : '—',
      reason: item.reason,
    }));
  }, [detailsData]);

  if (error) {
    throw new Error(error);
  }
  if (detailsError) {
    throw new Error(detailsError);
  }

  return (
    <Card
      loading={loading}
      border
      title="Timesheet Lateness By Week"
      description="Not submitted on time means no submission by Monday 10:00 AM ET following the week ending date (includes missing timesheets). Click a bar to see details below."
      floating
      padded={false}
      actionComponent={
        <div className="flex">
          <DateRangePicker
            loading={loading}
            startDate={startDate}
            endDate={endDate}
            handleDateChange={range => {
              const [nextStartDate, nextEndDate] = range;
              setStartDate(nextStartDate);
              setEndDate(nextEndDate);
            }}
          />
        </div>
      }
    >
      {loading ? (
        <TextLoader text="Loading Report" />
      ) : (
        <div>
          <div className="px-6 pt-4">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="week" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Bar
                  dataKey="lateCount"
                  name="Not Submitted On Time"
                  onClick={dataPoint => {
                    const endDateValue = dataPoint?.payload?.endDate;
                    if (endDateValue) {
                      setSelectedEndDate(endDateValue);
                    }
                  }}
                >
                  {chartData.map(entry => {
                    const isSelected = entry.endDate === selectedEndDate;
                    return (
                      <Cell
                        key={entry.endDate}
                        fill={isSelected ? '#1a4f8b' : '#2b6cb0'}
                      />
                    );
                  })}
                  <LabelList dataKey="lateCount" position="center" fill="#ffffff" />
                </Bar>
                <Line
                  type="monotone"
                  dataKey="avgLateCount"
                  name="4-Week Avg Not Submitted On Time"
                  stroke="#38a169"
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="px-6 pb-6">
            <div className="pb-2 text-gray-600">
              {selectedEndDate
                ? `Details for week ending ${moment(selectedEndDate, 'YYYYMMDD').format(
                    'MM/DD/YYYY',
                  )}`
                : 'Select a week to view details.'}
            </div>
            {detailsLoading ? (
              <TextLoader text="Loading Details" />
            ) : (
              <DataGrid rowData={detailRows} height="600px">
                <AgGridColumn field="firstName" headerName="First Name" />
                <AgGridColumn field="lastName" headerName="Last Name" />
                <AgGridColumn field="email" headerName="Email" />
                <AgGridColumn field="status" headerName="Timesheet Status" />
                <AgGridColumn field="submittedAt" headerName="Submitted At" />
                <AgGridColumn field="cutoff" headerName="Cutoff (ET)" />
                <AgGridColumn field="reason" headerName="Reason" />
              </DataGrid>
            )}
          </div>
        </div>
      )}
    </Card>
  );
};

export default TimesheetLatenessReport;
